import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { supabase } from "@/integrations/supabase/client";
import { SERVICE_LABEL, SERVICE_TYPES, type ServiceType } from "@/lib/services";

export const Route = createFileRoute("/_authenticated/super-admin/service-plans")({
  head: () => ({
    meta: [
      { title: "Service plans · Landoo Super Admin" },
      {
        name: "description",
        content:
          "Set separate pricing, limits and renewal rules for Sale, Rent, Lease and Parking.",
      },
    ],
  }),
  component: ServicePlansPage,
});

interface PlanRow {
  id: string;
  category: string;
  plan_key: string;
  plan_name: string;
  monthly_price: number;
  annual_price: number | null;
  listing_limit: number | null;
  image_limit: number | null;
  featured_allowance: number | null;
  verification_fee: number;
  grace_period_days: number;
  addon_discount_percent: number;
  renewal_months: number;
  active: boolean;
}

const inp =
  "w-full rounded-lg border border-border bg-background px-2 py-1.5 text-xs outline-none focus:border-brand";

function ServicePlansPage() {
  const [tab, setTab] = useState<ServiceType>("sale");
  const { data = [], refetch } = useQuery({
    queryKey: ["super-admin", "service-plans"],
    queryFn: async () => {
      const { data } = await supabase.from("category_plans").select("*").order("monthly_price");
      return (data as unknown as PlanRow[]) ?? [];
    },
  });
  const [draft, setDraft] = useState<Record<string, PlanRow>>({});
  useEffect(() => {
    setDraft(Object.fromEntries(data.map((p) => [p.id, p])));
  }, [data]);

  const rows = data.filter((p) => p.category === tab).map((p) => draft[p.id] ?? p);

  const patch = (id: string, k: keyof PlanRow, v: string) =>
    setDraft((d) => ({
      ...d,
      [id]: { ...d[id], [k]: v === "" ? null : isNaN(Number(v)) ? v : Number(v) },
    }));

  const save = async (row: PlanRow) => {
    const { error } = await supabase
      .from("category_plans")
      .update({
        plan_name: row.plan_name,
        monthly_price: row.monthly_price,
        annual_price: row.annual_price,
        listing_limit: row.listing_limit,
        image_limit: row.image_limit,
        featured_allowance: row.featured_allowance,
        verification_fee: row.verification_fee,
        grace_period_days: row.grace_period_days,
        addon_discount_percent: row.addon_discount_percent,
        renewal_months: row.renewal_months,
      })
      .eq("id", row.id);
    if (error) return toast.error(error.message);
    toast.success(`${SERVICE_LABEL[tab]} plan updated`);
    void refetch();
  };

  return (
    <DashboardShell title="Service plans" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <div className="flex flex-wrap gap-1.5">
        {SERVICE_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${tab === t ? "bg-brown text-primary-foreground" : "bg-secondary text-brown-dark"}`}
          >
            {SERVICE_LABEL[t]} plans
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Every service is priced and limited separately. Changes here never affect another service.
      </p>

      <div className="mt-4 grid gap-3">
        {rows.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
            No {SERVICE_LABEL[tab]} plans configured yet.
          </div>
        )}
        {rows.map((p) => (
          <div key={p.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
            <div className="text-sm font-bold text-brown-dark">{p.plan_name}</div>
            <div className="mt-3 grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
              <F label="Monthly price">
                <input
                  className={inp}
                  value={p.monthly_price ?? ""}
                  onChange={(e) => patch(p.id, "monthly_price", e.target.value)}
                />
              </F>
              <F label="Annual price">
                <input
                  className={inp}
                  value={p.annual_price ?? ""}
                  onChange={(e) => patch(p.id, "annual_price", e.target.value)}
                />
              </F>
              <F label="Listing limit">
                <input
                  className={inp}
                  value={p.listing_limit ?? ""}
                  onChange={(e) => patch(p.id, "listing_limit", e.target.value)}
                />
              </F>
              <F label="Image limit">
                <input
                  className={inp}
                  value={p.image_limit ?? ""}
                  onChange={(e) => patch(p.id, "image_limit", e.target.value)}
                />
              </F>
              <F label="Featured limit">
                <input
                  className={inp}
                  value={p.featured_allowance ?? ""}
                  onChange={(e) => patch(p.id, "featured_allowance", e.target.value)}
                />
              </F>
              <F label="Verification fee">
                <input
                  className={inp}
                  value={p.verification_fee ?? 0}
                  onChange={(e) => patch(p.id, "verification_fee", e.target.value)}
                />
              </F>
              <F label="Renewal (months)">
                <input
                  className={inp}
                  value={p.renewal_months ?? 1}
                  onChange={(e) => patch(p.id, "renewal_months", e.target.value)}
                />
              </F>
              <F label="Grace period (days)">
                <input
                  className={inp}
                  value={p.grace_period_days ?? 0}
                  onChange={(e) => patch(p.id, "grace_period_days", e.target.value)}
                />
              </F>
              <F label="Add-on discount %">
                <input
                  className={inp}
                  value={p.addon_discount_percent ?? 0}
                  onChange={(e) => patch(p.id, "addon_discount_percent", e.target.value)}
                />
              </F>
            </div>
            <button
              onClick={() => save(p)}
              className="mt-3 rounded-full bg-coral px-4 py-2 text-xs font-semibold text-white shadow-soft"
            >
              Save {SERVICE_LABEL[tab]} plan
            </button>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </label>
  );
}
